import React, { useRef, useEffect } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { deleteAbbreviation } from "../../../Api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

function AbbreviationsTable({
  data = [],
  startIndex = 0,
  handleEditClick,
  handleDeleteClick,
  selectedRows = [],
  handleSelectRow,
  sortableFields = [],
  handleSort,
  renderSortIcon,
  handleSelectAll,
  isAllSelected = false,   // 👈 new
  isSomeSelected = false,  // 👈 new
}) {
  const navigate = useNavigate();
  const headerCheckboxRef = useRef(null);

  // Determine selection state for this page
  const currentPageIds = data.map((row) => row.record_id);

  const selectedOnThisPage = currentPageIds.filter((id) =>
    selectedRows.includes(id)
  );

  const allSelected =
    data.length > 0 && selectedOnThisPage.length === data.length;

  const someSelected =
    selectedOnThisPage.length > 0 && selectedOnThisPage.length < data.length;

useEffect(() => {
  if (!headerCheckboxRef.current) return;

  // Force the header checkbox to be either checked or unchecked
  // (no visual indeterminate state). The `checked` prop is driven
  // by `isAllSelected` so clearing `indeterminate` guarantees the
  // checkbox appears strictly checked or unchecked everywhere.
  headerCheckboxRef.current.indeterminate = false;
}, [isAllSelected, isSomeSelected, someSelected, allSelected]);

  
  const handleDelete = async (recordId) => {
    try {
      const requestData = {
        record_id: recordId,
        correl_id: localStorage.getItem("correl_id") || "",
      };
      console.log(requestData);

      const result = await Swal.fire({
        text: `Are you sure you want to delete?`,
        icon: "warning",
        confirmButtonText: "Yes",
        showCancelButton: true,
        cancelButtonText: "No",
      });

      if (result.isConfirmed) {
        Swal.fire({
          html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const apiData = await deleteAbbreviation(requestData);
        Swal.close();

        if (apiData.status === "S" && apiData.result_code === 200) {
          Swal.fire({
            text: "Abbreviation deleted successfully",
            icon: "success",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            handleDeleteClick();
          });
        } else if (
          apiData.status === "F" &&
          (apiData.message === "Unauthorized - Missing token" ||
            apiData.message === "Unauthorized - Invalid token")
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else {
          Swal.fire({
            text: apiData.message || "Failed to delete abbreviation data",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error deleting abbreviation data:", error);
      Swal.fire({
        text: "An error occurred while deleting the abbreviation data",
        icon: "error",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  if (!Array.isArray(data)) {
    return (
      <div className="table-responsive mb-3">
        <table className="table table-bordered table-striped">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Abbreviation</th>
              <th>Replace Word</th>
              <th>Created By</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="6" className="text-center">
                No data available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="table-responsive mb-3">
      <table className="table table-bordered table-striped">
        <thead className="table-dark">
          <tr>
            {/* Select-all checkbox in header */}
            <th>
              {/* <input
                type="checkbox"
                ref={headerCheckboxRef}
                checked={allSelected}
                onChange={() => {
                  // If currently indeterminate (some selected), the next click should deselect all (false).
                  // Otherwise (checked or unchecked), it should simply toggle based on the current allSelected state.
                  const shouldSelect = someSelected ? false : !allSelected;

                  handleSelectAll(shouldSelect, data);
                  
                }}
                style={{ fontSize: '1.5rem' }}
              /> */}
              <input
                type="checkbox"
                ref={headerCheckboxRef}
                checked={isAllSelected}
                onChange={() => {
                  // Use the global selection flags so the header toggles
                  // selection for ALL pages. Clicking when some are
                  // selected will deselect all; otherwise toggle all.
                  const shouldSelect = isSomeSelected ? false : !isAllSelected;

                  handleSelectAll(shouldSelect);
                }}
                style={{ fontSize: "1.5rem" }}
              />
            </th>

            <th>#</th>
            <th
              onClick={() =>
                handleSort && handleSort("ABBREVIATION", sortableFields)
              }
              style={{
                cursor: sortableFields.includes("ABBREVIATION")
                  ? "pointer"
                  : "default",
                userSelect: "none",
                position: "relative",
              }}
            >
              Abbreviation
              {renderSortIcon && renderSortIcon("ABBREVIATION", sortableFields)}
            </th>
            <th
              onClick={() =>
                handleSort && handleSort("REPLACE_WORD", sortableFields)
              }
              style={{
                cursor: sortableFields.includes("REPLACE_WORD")
                  ? "pointer"
                  : "default",
                userSelect: "none",
                position: "relative",
              }}
            >
              Replace Word
              {renderSortIcon && renderSortIcon("REPLACE_WORD", sortableFields)}
            </th>
            <th>Created By</th>

            {!allSelected && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={row.record_id}>
                {/* Row-level checkbox */}
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.record_id)}
                    onChange={() => handleSelectRow(row.record_id)}
                  />
                </td>
                <td>{startIndex + index + 1}</td>
                <td>{row.name || "N/A"}</td>
                <td>{row.abbreviations || "N/A"}</td>
                <td>{row.created_by || "N/A"}</td>
                {!allSelected && (
                  <td className="text-center">
                    <div
                      style={{
                        // Check if the current row (row.record_id) is selected
                        // If it is selected, hide the buttons. If not, show them.
                        visibility: selectedRows.includes(row.record_id)
                          ? "hidden"
                          : "visible",
                      }}
                    >
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditClick(row)}
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(row.record_id)}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AbbreviationsTable;
